import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import { ToastContainer } from '../components/UndoToast';
import './AdminPage.css';

function AdminPage() {
  const { code } = useParams();
  const [session, setSession] = useState(null);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasNewVotes, setHasNewVotes] = useState(false);
  const [knownVoters, setKnownVoters] = useState([]);
  
  // Use refs to avoid stale closures in setInterval
  const showResultsRef = useRef(false);
  const lastVoteCountRef = useRef(0);
  
  const [copied, setCopied] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsMaxVoters, setSettingsMaxVoters] = useState(20);
  const [settingsAnonymous, setSettingsAnonymous] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  
  const voteUrl = `${window.location.origin}/vote/${code}`;

  useEffect(() => {
    loadSession();
    
    // Poll for updates every 5 seconds
    const interval = setInterval(() => {
      loadSession();
    }, 5000);
    
    return () => {
      clearInterval(interval);
    };
  }, [code]);

  const loadSession = async () => {
    try {
      const sessionData = await api.getSession(code);
      
      // Check if new votes arrived while viewing results
      if (showResultsRef.current && sessionData.vote_count > lastVoteCountRef.current) {
        setHasNewVotes(true);
      }
      
      setSession(sessionData);
      setLoading(false);
      setError('');
    } catch (err) {
      setError('Session not found');
      setLoading(false);
    }
  };

  const loadResults = async () => {
    try {
      const resultsData = await api.getResults(code);
      setResults(resultsData);
      setShowResults(true);
      showResultsRef.current = true;
      lastVoteCountRef.current = resultsData.total_votes;
      setHasNewVotes(false);
      
      // Snapshot: remember who we've seen so far
      const voterNames = resultsData.voters.map(v => v.name);
      setKnownVoters(voterNames);
    } catch (err) {
      setError('Failed to load results');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(voteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDeleteVote = async (voterName) => {
    setConfirmModal({
      title: 'Delete Vote',
      message: `Are you sure you want to delete the vote from "${voterName}"?`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.deleteVote(code, voterName);
          await loadSession();
          if (showResults) {
            await loadResults();
          }
        } catch (err) {
          setError('Failed to delete vote. Please try again.');
          setTimeout(() => setError(''), 3000);
        }
      }
    });
  };

  const addToast = useCallback((message, undoAction) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, undoAction }]);
    return id;
  }, []);

  const handleToastUndo = useCallback(async (toastId) => {
    const toast = toasts.find(t => t.id === toastId);
    setToasts(prev => prev.filter(t => t.id !== toastId));
    if (toast?.undoAction) {
      await toast.undoAction();
      await loadSession();
    }
  }, [toasts]);

  const handleToastExpire = useCallback((toastId) => {
    setToasts(prev => prev.filter(t => t.id !== toastId));
  }, []);

  const handleCloseSession = async () => {
    try {
      await api.closeSession(code);
      await loadSession();
      addToast('Session closed', async () => {
        await api.reopenSession(code);
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close session');
    }
  };

  const handleReopenSession = async () => {
    try {
      await api.reopenSession(code);
      await loadSession();
      addToast('Session reopened', async () => {
        await api.closeSession(code);
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reopen session');
    }
  };

  const openSettings = () => {
    if (session) {
      setSettingsMaxVoters(session.max_voters);
      setSettingsAnonymous(session.anonymous || false);
    }
    setShowSettings(true);
  };

  const saveSettings = async () => {
    try {
      await api.updateSettings(code, {
        max_voters: settingsMaxVoters,
        anonymous: settingsAnonymous
      });
      setShowSettings(false);
      await loadSession();
    } catch (err) {
      alert('Failed to save settings. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1>Confidence Vote Session</h1>
          <div className="admin-header-actions">
            <button className="settings-btn" onClick={openSettings}>⚙ Settings</button>
            <button className="export-btn" onClick={() => window.open(api.exportSessionUrl(code, 'csv'), '_blank')}>📥 Export CSV</button>
            {!session.closed && (
              <button className="close-session-btn" onClick={handleCloseSession}>Close Session</button>
            )}
            {session.closed && (
              <button className="reopen-session-btn" onClick={handleReopenSession}>Reopen Session</button>
            )}
          </div>
        </div>

        {showSettings && (
          <div className="modal-overlay" onClick={() => setShowSettings(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>Session Settings</h2>
              
              <div className="setting-group">
                <label htmlFor="maxVoters">Max Voters:</label>
                <div className="slider-with-input">
                  <input 
                    type="range"
                    id="maxVoters"
                    min="5"
                    max="100"
                    value={settingsMaxVoters}
                    onChange={(e) => setSettingsMaxVoters(parseInt(e.target.value))}
                  />
                  <input
                    type="number"
                    className="slider-number"
                    min="5"
                    max="100"
                    value={settingsMaxVoters}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (!isNaN(val)) setSettingsMaxVoters(Math.min(100, Math.max(5, val)));
                    }}
                  />
                </div>
              </div>

              <div className="setting-group">
                <label className="toggle-label">
                  <span>Anonymous Mode</span>
                  <div className={`toggle ${settingsAnonymous ? 'active' : ''}`}
                    onClick={() => setSettingsAnonymous(!settingsAnonymous)}
                  >
                    <div className="toggle-knob"></div>
                  </div>
                </label>
                <p className="setting-hint">
                  {settingsAnonymous 
                    ? 'Voters will not need to enter their name' 
                    : 'Voters must enter their name'}
                </p>
              </div>

              <div className="modal-actions">
                <button className="modal-save-btn" onClick={saveSettings}>Save</button>
                <button className="modal-cancel-btn" onClick={() => setShowSettings(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        
        <div className="session-info">
          <h2 className="session-code">Code: {code}</h2>
          
          <div className="share-section">
            <div className="share-content">
              <div className="share-link">
                <p><strong>Share this link:</strong></p>
                <div className="link-container">
                  <input 
                    type="text" 
                    value={voteUrl} 
                    readOnly 
                    className="vote-link"
                  />
                  <button onClick={copyLink} className={`copy-btn ${copied ? 'copied' : ''}`}>
                    {copied ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              <div className="qr-section">
                <p><strong>Or scan QR code:</strong></p>
                <QRCodeSVG 
                  value={voteUrl} 
                  size={150}
                  level="M"
                  includeMargin={true}
                />
              </div>
            </div>
          </div>
        </div>
        
        {session.closed && (
          <div className="session-closed-banner">
            🔒 Session Closed — No more votes accepted
          </div>
        )}

        <div className="voters-section">
          <h3>
            {session.vote_count === 0 ? 'Waiting for votes...' : `Votes: ${session.vote_count}`}
          </h3>
          
          {session.voters && session.voters.length > 0 && (
            <div className="voters-list">
              <h4>Voters:</h4>
              <ul>
                {session.voters.map((voter, index) => {
                  const isNew = knownVoters.length > 0 && !knownVoters.includes(voter);
                  return (
                    <li key={index} className={isNew ? 'new-voter' : ''}>
                      • {voter}
                      {isNew && <span className="new-badge">NEW</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
        
        <button 
          className="show-results-btn"
          onClick={loadResults}
          disabled={session.vote_count === 0}
        >
          {showResults ? 'Refresh Results' : 'Show Results'}
        </button>
        
        {hasNewVotes && (
          <div className="new-votes-alert">
            <p>📊 New vote(s) received! Click "Refresh Results" to see updated data.</p>
          </div>
        )}
        
        {showResults && results && (
          <div className="results-section">
            <h2>Results</h2>
            
            <div className="vote-breakdown">
              {['5', '4', '3', '2', '1'].map(level => {
                const votersForLevel = results.voters_by_level?.[level] || [];
                const tooltip = votersForLevel.length > 0 
                  ? votersForLevel.join(', ') 
                  : 'No votes';
                
                return (
                  <div key={level} className="vote-count-row">
                    <span className="vote-label">{level}'s:</span>
                    <div className="vote-bar">
                      <div 
                        className="vote-bar-fill"
                        style={{ width: `${results.total_votes > 0 ? (results.vote_counts[level] / results.total_votes) * 100 : 0}%` }}
                        title={tooltip}
                      >
                        {results.vote_counts[level]}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className={`confidence-score ${results.color}`}>
              <h3>Confidence Score: {results.average.toFixed(2)} / 5.00</h3>
              <p className="vote-count-text">(Based on {results.total_votes} total votes)</p>
              <p className="confidence-message">{results.message}</p>
            </div>
            
            {results.voters && results.voters.length > 0 && (
              <div className="detailed-votes">
                <h4>Detailed Votes:</h4>
                <ul>
                  {results.voters.map((voter, index) => {
                    const isNew = knownVoters.length > 0 && !knownVoters.includes(voter.name);
                    return (
                      <li key={index} className={isNew ? 'new-voter' : ''}>
                        <span className="voter-info">
                          {voter.name}: <strong>{voter.vote}</strong>
                          {isNew && <span className="new-badge">NEW</span>}
                        </span>
                        <button 
                          className="delete-vote-btn"
                          onClick={() => handleDeleteVote(voter.name)}
                          title="Delete this vote"
                        >
                          Delete
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <ToastContainer
        toasts={toasts}
        onUndo={handleToastUndo}
        onExpire={handleToastExpire}
      />

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          variant={confirmModal.variant}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
}

export default AdminPage;
